import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { baseUrl } from '../../constant/config';
import ConnectTokenDialog from './ConnectTokenDialog';
import { PLATFORM_META } from './shareMeta';

// Below this, the token is close enough to lapsing that it needs attention
// before it takes publishing down without warning.
const WARN_DAYS = 14;

const formatDay = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value; // Meta's own wording, unparsed.
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Health of the access token behind the current tab.
 *
 * A Meta token is what quietly breaks this integration: it lapses, or it was
 * issued without a permission only some calls need, and publishing starts
 * failing with no warning. Showing it at the top means the problem is visible
 * before somebody tries to publish.
 */
export default function TokenStatusBanner({ platform, enabled }) {
  const meta = PLATFORM_META[platform] || {};
  const [showDetail, setShowDetail] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['platformToken', platform],
    async () => {
      const { data } = await axios.get(
        `${baseUrl}/api/videos/platform-token?platform=${platform}`
      );
      return data.data;
    },
    { enabled, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: false }
  );

  if (!enabled) return null;

  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Skeleton height={52} variant="rounded" />
      </Box>
    );
  }

  // The endpoint answers with a status even for a dead token, so reaching here
  // means the request itself failed.
  if (isError) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Could not check the {meta.label} token:{' '}
        {error?.response?.data?.message || error?.message}
      </Alert>
    );
  }

  if (!data) return null;

  const { valid, neverExpires, daysLeft, expiresAt, expiredAt, missingScopes, scopesKnown } = data;

  const expiringSoon = valid && typeof daysLeft === 'number' && daysLeft <= WARN_DAYS;
  const severity = !valid ? 'error' : expiringSoon || missingScopes.length ? 'warning' : 'success';

  // An expired token still reports when it lapsed - prefer that over a bare
  // "not valid", which says nothing about what to do.
  const lapsed = expiredAt || (expiresAt && new Date(expiresAt) < new Date() ? expiresAt : null);

  let headline;
  if (!valid) {
    headline = lapsed
      ? `${meta.label} token expired on ${formatDay(lapsed)}`
      : `${meta.label} token is not valid`;
  } else if (neverExpires) {
    headline = `${meta.label} token is valid and does not expire`;
  } else if (typeof daysLeft === 'number') {
    headline = `${meta.label} token expires in ${daysLeft} ${
      daysLeft === 1 ? 'day' : 'days'
    } (${formatDay(expiresAt)})`;
  } else {
    headline = `${meta.label} token is valid`;
  }

  return (
    <>
    <Alert
      severity={severity}
      sx={{ mb: 2 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
          {platform === 'facebook' && (
            <Button
              color="inherit"
              size="small"
              variant={valid ? 'text' : 'outlined'}
              onClick={() => setConnecting(true)}
            >
              {valid ? 'Replace token' : 'Connect token'}
            </Button>
          )}
          <Button color="inherit" size="small" onClick={() => setShowDetail((v) => !v)}>
            {showDetail ? 'Hide' : 'Details'}
          </Button>
          <Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>
            Recheck
          </Button>
        </Stack>
      }
    >
      <AlertTitle sx={{ mb: showDetail ? 1 : 0 }}>{headline}</AlertTitle>

      {!valid && (
        <Typography variant="body2">
          Publishing and everything on this tab will fail until a working token is connected. Use{' '}
          <strong>Connect token</strong> - the server turns a short-lived token into one that does
          not expire, so this should not keep happening.
        </Typography>
      )}

      {missingScopes.length > 0 && (
        <Typography variant="body2" sx={{ mt: valid ? 0 : 1 }}>
          <strong>
            Missing {missingScopes.length === 1 ? 'permission' : 'permissions'}:{' '}
            {missingScopes.join(', ')}
          </strong>
          . {valid ? 'Some actions here will be refused by Meta.' : 'Tick these when generating the replacement, or publishing will still fail.'}
        </Typography>
      )}

      {isFetching && <LinearProgress sx={{ mt: 1 }} />}

      <Collapse in={showDetail}>
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {data.error && (
            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
              <strong>Meta says:</strong> {data.error}
            </Typography>
          )}
          {data.appName && (
            <Typography variant="caption">
              <strong>App:</strong> {data.appName}
              {data.appId ? ` (${data.appId})` : ''}
            </Typography>
          )}
          {data.type && (
            <Typography variant="caption">
              <strong>Token type:</strong> {data.type}
            </Typography>
          )}
          {data.issuedAt && (
            <Typography variant="caption">
              <strong>Issued:</strong> {formatDay(data.issuedAt)}
            </Typography>
          )}
          {data.dataAccessExpiresAt && (
            <Typography variant="caption">
              <strong>Data access expires:</strong> {formatDay(data.dataAccessExpiresAt)}
            </Typography>
          )}

          {scopesKnown ? (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                <strong>Granted permissions</strong>
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {data.scopes.length === 0 && <Typography variant="caption">none</Typography>}
                {data.scopes.map((s) => (
                  <Chip key={s} size="small" label={s} variant="outlined" />
                ))}
                {missingScopes.map((s) => (
                  <Chip key={s} size="small" color="error" label={`${s} (missing)`} />
                ))}
              </Stack>
            </Box>
          ) : (
            <Typography variant="caption">
              <strong>Permissions could not be read.</strong>{' '}
              {data.appTokenError
                ? `Meta rejected the app credentials: ${data.appTokenError} - check FACEBOOK_APP_ID and FACEBOOK_APP_SECRET match the app that issued this token.`
                : 'Set FACEBOOK_APP_ID and FACEBOOK_APP_SECRET to see them.'}
            </Typography>
          )}

          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Tokens are managed in the{' '}
            <Link
              href="https://developers.facebook.com/tools/debug/accesstoken/"
              target="_blank"
              rel="noreferrer"
            >
              Meta access token debugger
            </Link>
            .
          </Typography>
        </Stack>
      </Collapse>
    </Alert>

    <ConnectTokenDialog
      open={connecting}
      onClose={() => {
        setConnecting(false);
        refetch();
      }}
    />
    </>
  );
}

TokenStatusBanner.propTypes = {
  platform: PropTypes.oneOf(['facebook', 'instagram']).isRequired,
  enabled: PropTypes.bool,
};
